import React, { useContext } from 'react'

import {
  useCoordinator,
  useCurrentUser,
  useProject,
} from '@builddocs/data-management'
import {
  EmploymentButton,
  NotificationsButtonWithPopover,
} from '@builddocs/exdoc-app/feature-notifications'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import {
  AppBar,
  Divider,
  Stack,
  Toolbar,
  IconButton,
  Box,
  Theme,
  Button,
} from '@mui/material'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import useMediaQuery from '@mui/material/useMediaQuery'
import { matchPath, useLocation } from 'react-router-dom'

import SideNavigation from '../side-navigation/SideNavigation'

import AddUserPosition from './add-user-position/AddUserPosition'
import AddUsersModal from './AddUsersModal'
import LogoButton from './atoms/LogoButton'
import ProcessUsers from './CreateOrganizationForUser'
import Navigation from './Navigation'
import ProfileField from './ProfileField'
import { FeatureFlagContext } from '@builddocs/system'

const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent)

const projectIds = new Set([
  '7617536f-a58e-4055-bfe0-95e52e7e7511', // Захарковский карьер
  'df593ff2-c98d-11ee-8716-d00d126464f0', // 2 этап берегоукрепл.
  'de0d08b5-32b0-11ee-8167-d00d126464f0', // В19
  'ab76b1aa-3505-11ee-8167-d00d126464f0', // Т08
  '765e0973-3209-11ee-8167-d00d126464f0', // В11-В12
  '19504802-32c6-11ee-8167-d00d126464f0', // В20
  '25353e0c-351c-11ee-8167-d00d126464f0', // В21
])

export const Header = () => {
  const { ErrorTest } = useContext(FeatureFlagContext)
  const [isOpenDrawer, setIsOpenDrawer] = React.useState(false)
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))

  const location = useLocation()

  const [matchProjectsPage, matchOrganizations, matchProfile] = [
    matchPath('/sakura', location.pathname),
    matchPath('/organization/*', location.pathname),
    matchPath('/my-account', location.pathname),
  ]

  const user = useCurrentUser()
  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event &&
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      setIsOpenDrawer(open)
    }

  const { projectId = '' } = useCoordinator()

  let showNotification = false

  const project = useProject(projectId)

  let content

  if (project?.project?.integrations?.includes('PIK')) {
    content = (
      <div>
        Уважаемые партнеры ГК ПИК! ООО «ПИК-УК» представляет Услуги по
        формированию исполнительной документации.
        <br />
        За более подробной информацией обращаться к руководителю группы ЦТУ
        Кузьминой И.А. +7 916 774 98 23.
      </div>
    )

    showNotification = true
  }
  if (projectIds.has(projectId)) {
    content = (
      <div>
        Уважаемые пользователи! С 6 февраля 2025 года у ООО "ГАРДА" изменилось
        наименование на ООО "БАЙРАК ИНШААТ". До 30 июня 2025 года в системе
        будут отображаться старые реквизиты.
        <br />
        Пожалуйста, в этот период оформите и подпишите все акты на работы,
        выполненные до смены реквизитов. С 01 июля 2025 года реквизиты будут
        обновлены на актуальные для ООО "БАЙРАК ИНШААТ"
      </div>
    )

    showNotification = true
  }

  return (
    <React.Fragment>
      {!isDesktop &&
        !matchProjectsPage &&
        !matchProfile &&
        !matchOrganizations && (
          <SwipeableDrawer
            PaperProps={{
              sx: {
                width: 'calc(100% - 96px)',
                maxWidth: 512,
                minWidth: { xs: '100vw', sm: 196 },
              },
            }}
            anchor="left"
            disableBackdropTransition={!iOS}
            disableDiscovery={iOS}
            open={isOpenDrawer}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
          >
            <SideNavigation onClose={() => setIsOpenDrawer(false)} />
          </SwipeableDrawer>
        )}
      <AppBar
        elevation={0}
        position="relative"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          height: showNotification ? 112 : 56,
          minHeight: showNotification ? 112 : 56,
        }}
      >
        {showNotification && (
          <Box
            sx={{
              width: '100%',
              position: 'fixed',
              top: 0,
              height: '56px',
              minHeight: '56px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly',
              zIndex: 1250,
              bgcolor: '#0034AD',
              paddingLeft: '16px',
              paddingRight: '16px',
              py: '8px',
              textAlign: 'center',
            }}
          >
            {content}
          </Box>
        )}

        <Toolbar
          sx={{
            px: { xs: 0 },
            position: 'fixed',
            top: showNotification ? 56 : 0,
            minHeight: { xs: 56 },
            height: { xs: 56 },
            display: 'grid',
            gridTemplateColumns: {
              lg: '1fr auto 1fr',
              md: 'auto auto auto',
              xs: '1fr auto 1fr',
            },
            gridTemplateRows: '1fr auto',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            color: 'text.primary',
            zIndex: theme => theme.zIndex.appBar + 10,
            width: '100%',
          }}
        >
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={0.5}
            sx={{ width: 'fit-content', ml: 0.5 }}
          >
            {!isDesktop &&
            !matchProjectsPage &&
            !matchProfile &&
            !matchOrganizations ? (
              <IconButton sx={{ ml: 0.5 }} onClick={toggleDrawer(true)}>
                <MenuRoundedIcon sx={{ color: 'black' }} />
              </IconButton>
            ) : (
              <LogoButton />
            )}
            {(user.user.userId === 'decde69c-b16b-11ef-bf38-d00d126464f0' ||
              user.user.userId === 'b5a607cd-323b-11eb-bd3d-d00d126464f0') && (
              <AddUsersModal />
            )}
            {(user.user.userId === 'decde69c-b16b-11ef-bf38-d00d126464f0' ||
              user.user.userId === 'b5a607cd-323b-11eb-bd3d-d00d126464f0') && (
              <ProcessUsers />
            )}
            {(user.user.userId === 'decde69c-b16b-11ef-bf38-d00d126464f0' ||
              user.user.userId === 'b5a607cd-323b-11eb-bd3d-d00d126464f0') && (
              <AddUserPosition />
            )}
            {ErrorTest && (
              <>
                <Button
                  onClick={() => {
                    throw new Error('Тест Синхронная')
                  }}
                >
                  Синхронная
                </Button>
                <Button
                  onClick={() => {
                    setTimeout(() => {
                      throw new Error('Тест Асинхронная')
                    }, 0)
                  }}
                >
                  Асинхронная
                </Button>
              </>
            )}
          </Stack>
          <Navigation />
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="flex-end"
            spacing={0.5}
          >
            <NotificationsButtonWithPopover placement="bottom" size="large" />
            <EmploymentButton placement="bottom" />
            <ProfileField />
          </Stack>
          <Divider
            sx={{
              width: '100%',
              gridColumn: '1/4',
              position: 'relative',
              top: -1,
            }}
          />
        </Toolbar>
        <Divider sx={{ height: 56, borderColor: 'transparent' }} />
      </AppBar>
    </React.Fragment>
  )
}

export default Header
